/* eslint-disable react/prop-types */
import React, { useEffect, useRef } from 'react';
import styles from './styles.module.scss';

const SCROLL_TIMEOUT = 50;

/**
 * Generic carousel/slider that can accept any type of card.
 *
 * @param {object} props - The component props.
 * @param {Array} props.cards - The cards to display in the carousel.
 * @param {number} [props.currentCardIndex] - The position of the card currently in focus within the carousel.
 * @param {Function} [props.onResize] - The function that resizes the carousel when the user resizes the browser.
 * @param {Function} [props.onScroll] - The function that resizes the carousel when the user scrolls.
 * @param {Function} [props.renderCard] - The function that renders each card.
 * @param {number} [props.scrollDistance] - The distance the carousel travels when scrolling.
 * @returns {JSX.Element|null}
 */
const Carousel = ({
  cards = [],
  currentCardIndex = 0,
  onResize = () => {},
  onScroll = () => {},
  renderCard = () => {},
  scrollDistance = 280,
}) => {
  const refContainer = useRef(null);
  const refSlide = useRef(null);
  const scrollTimeoutRef = useRef(null);


  useEffect(() => {
    refSlide?.current?.scrollTo?.({
      left: currentCardIndex * scrollDistance,
      behavior: 'smooth',
    });
  }, [currentCardIndex, scrollDistance]);

  useEffect(() => {
    // assign to variable for cleanup function
    const slideNode = refSlide?.current;

    /**
     *
     */
    const handleResize = () => {
      if (refContainer?.current && slideNode) {
        const containerWidth = refContainer.current.clientWidth;
        const slideWidth = slideNode.scrollWidth;
        const slideLeft = slideNode.scrollLeft;
        const leftScrollPossible = slideLeft > 0;
        const rightScrollPossible = slideWidth - slideLeft > containerWidth;
        onResize(leftScrollPossible, rightScrollPossible);
      }
    };

    /**
     *
     */
    const handleScroll = () => {
      handleResize();

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      /**
       *
       */
      const calcNewIndex = () => {
        const slideLeft = slideNode.scrollLeft;
        const slideRight = slideNode.scrollWidth - slideLeft - slideNode.clientWidth;

        if (slideRight > 0) {
          return Math.round(slideLeft / scrollDistance);
        }

        // the current index should never reach the last card when more than 1 card can fit in view
        const containerWidth = refContainer.current?.clientWidth ?? 0;
        const numberOfCardsThatFitInView = Math.floor(containerWidth / scrollDistance) || 1;

        return Math.max(cards.length - numberOfCardsThatFitInView, 0);
      };

      scrollTimeoutRef.current = setTimeout(() => {
        onScroll(calcNewIndex());
      }, SCROLL_TIMEOUT);
    };

    window.addEventListener('resize', handleResize);
    if (slideNode) {
      slideNode.addEventListener('scroll', handleScroll);
    }

    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);

      if (slideNode) {
        slideNode.removeEventListener('scroll', handleScroll);
      }
    };
  }, [onResize, onScroll, scrollDistance, cards.length]);

  if (!cards.length) {
    return null;
  }

  return (
    <div className={styles.carousel} data-testid="carousel" ref={refContainer}>
      <div
        className={styles.carousel__slide}
        ref={refSlide}
        data-testid="carousel-slide"
      >
        {cards.map((card, index) => renderCard(
          card,
          index,
          `${styles.carousel__card}`,
        ))}
      </div>
    </div>
  );
};

export { Carousel };
